import { useState } from 'react';
import { useGT } from 'gt-react';
import { useUploadEntityDocument } from '@/api/rpc/hooks/core/useEntityDocuments';
import { useAddArDepositBatchDocuments } from '@/api/rpc/hooks/accounts-receivable/useArDepositBatches';
import { useToast } from 'src/context/ToastContext';
import { validateFilesForUpload } from 'src/lib/file-upload-validation';
import { useUploadProgressRows } from '@/hooks/useUploadProgressRows';

/** What a check deposit takes: check scans and remittances. */
export const ACCEPTED_DEPOSIT_FILES = 'image/*,application/pdf';

/** Files one drop may carry: a deposit's whole stack of scans at once. */
export const MAX_DEPOSIT_FILES_PER_DROP = 100;

/** entityType the upload flow tags check-deposit files with. */
const DEPOSIT_BATCH_ENTITY_TYPE = 'ar_deposit_batch';

/**
 * Upload check scans and remittances into a check deposit: each file goes to
 * storage (useUploadEntityDocument, which toasts per file), then every stored
 * file is added to the deposit in one call, which starts reading them. The
 * deposit is named per call, so a page can create one and fill it at once.
 */
export function useUploadDepositFiles() {
  const gt = useGT();
  const { showToast } = useToast();
  const { progress, track, reset } = useUploadProgressRows();
  const upload = useUploadEntityDocument();
  const addDocuments = useAddArDepositBatchDocuments();

  // Files upload in parallel, and a mutation's isPending follows only its
  // latest call, so the whole drop is tracked here.
  const [dropsInFlight, setDropsInFlight] = useState(0);
  const isUploading = dropsInFlight > 0;

  /** Uploads one file to storage. Never throws: a failure marks its row. */
  function uploadOne(
    depositBatchId: string,
    file: File,
  ): Promise<string | null> {
    return track(file, async () => {
      const result = await upload.mutateAsync({
        entityType: DEPOSIT_BATCH_ENTITY_TYPE,
        entityId: depositBatchId,
        file,
      });
      return result.documentId;
    });
  }

  /** Resolves true when at least one file was added to the deposit. */
  function uploadFiles(
    depositBatchId: string,
    files: File[],
  ): Promise<boolean> {
    setDropsInFlight((count) => count + 1);
    // .finally, not try/finally: the React Compiler cannot lower a try
    // without a catch.
    return uploadDrop(depositBatchId, files).finally(() =>
      setDropsInFlight((count) => count - 1),
    );
  }

  async function uploadDrop(
    depositBatchId: string,
    files: File[],
  ): Promise<boolean> {
    const { validFiles, errors } = validateFilesForUpload(
      files,
      MAX_DEPOSIT_FILES_PER_DROP,
    );
    for (const rejection of errors) {
      showToast({
        color: 'error',
        title: gt('Upload failed', {
          context: 'toast title, a dropped check deposit file was rejected',
        }),
        message: `${rejection.file.name}: ${rejection.error}`,
      });
    }
    const documentIds = (
      await Promise.all(
        validFiles.map((file) => uploadOne(depositBatchId, file)),
      )
    ).filter((id): id is string => id !== null);
    if (documentIds.length === 0) return false;
    try {
      await addDocuments.mutateAsync({ id: depositBatchId, documentIds });
      reset();
      return true;
    } catch (error) {
      showToast({
        color: 'error',
        title: gt('Could not add the files to this deposit', {
          context:
            'toast title, uploaded files could not be added to a check deposit',
        }),
        message: error instanceof Error ? error.message : '',
      });
      return false;
    }
  }

  return { uploadFiles, progress, isUploading };
}
